/**
 * FreeformPage — full-page route at /freeform.
 *
 * Mounted as a full-page ProtectedRoute (not a modal) because:
 * - The flow has multiple discrete steps with async loading states.
 * - The review step needs the full viewport to display candidate cards.
 * - Paste Experience is a distinct task context from Library browsing.
 *
 * Navigation: Library → /freeform via useNavigate.
 * After commit: result shown inline here; user returns via "Back to Library" link.
 */

import { type FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Sidebar from '../components/Sidebar'
import ResolveReview from '../components/library/ResolveReview'
import {
  useFreeformStore,
  type ActiveQuestion,
  type CandidateReview,
} from '../stores/freeformStore'

// ────────────────────────────────────────────────────────────────────────────
// Completion message (all 4 cases)
// ────────────────────────────────────────────────────────────────────────────

function completionMessage(added: number, enriched: number): string {
  const plural = (n: number) => `proof${n !== 1 ? 's' : ''}`
  if (added > 0 && enriched > 0)
    return `✓ ${added} ${plural(added)} added, ${enriched} enriched`
  if (added > 0)
    return `✓ ${added} ${plural(added)} added (no additional details were found)`
  if (enriched > 0)
    return `✓ ${enriched} ${plural(enriched)} enriched (no new accomplishments were added)`
  return 'No new accomplishments were added (they may already exist in your library)'
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-views
// ────────────────────────────────────────────────────────────────────────────

function InputStep() {
  const { start, loading, error } = useFreeformStore()
  const [text, setText] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!text.trim() || loading) return
    await start(text)
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl">
      <h1 className="text-lg font-semibold text-stone-800 mb-1">Paste Experience</h1>
      <p className="text-xs text-stone-400 mb-5">
        Proof of Work — Your record of professional impact
      </p>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={12}
        disabled={loading}
        placeholder="Paste your CV, LinkedIn summary, performance review, or any professional narrative…"
        className="w-full rounded border border-stone-200 px-4 py-3 text-sm text-stone-800 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none resize-y disabled:opacity-60 disabled:cursor-not-allowed"
      />
      <p className="mt-2 text-xs text-stone-400">
        Paste your experience. We'll extract accomplishments and may ask a few questions to improve them.
      </p>

      {error && (
        <p className="mt-3 text-xs text-red-600 rounded border border-red-200 bg-red-50 px-3 py-2">
          {error}
        </p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="submit"
          disabled={!text.trim() || loading}
          className="rounded px-4 py-2 text-sm font-medium text-white bg-stone-800 hover:bg-stone-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Analyse
        </button>
        <Link
          to="/library"
          className="text-xs text-stone-500 hover:text-stone-700 transition-colors"
        >
          Back to Library
        </Link>
      </div>
    </form>
  )
}

function ExtractingStep() {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <span className="animate-spin inline-block w-6 h-6 border-2 border-stone-300 border-t-stone-700 rounded-full" />
      <p className="text-sm text-stone-500">Extracting accomplishments…</p>
    </div>
  )
}

function InterrogationStep() {
  const { currentQuestion, loading, submitAnswer, skipQuestion } = useFreeformStore()
  const [answer, setAnswer] = useState('')

  const q = currentQuestion as ActiveQuestion | null

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!q || !answer.trim() || loading) return
    const trimmed = answer.trim()
    setAnswer('')
    await submitAnswer(q.temp_id, trimmed)
  }

  const handleSkip = async () => {
    if (!q || loading) return
    await skipQuestion(q.temp_id)
  }

  if (!q) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <span className="animate-spin inline-block w-6 h-6 border-2 border-stone-300 border-t-stone-700 rounded-full" />
        <p className="text-sm text-stone-500">Loading question…</p>
      </div>
    )
  }

  return (
    <div className="max-w-xl">
      <p className="text-xs text-stone-400 mb-1">Improving: {q.item_label}</p>
      <p className="text-xs font-medium text-amber-600 uppercase tracking-wider mb-4">
        Question {q.question_number} of {q.max_questions} for this item
      </p>

      <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm space-y-4">
        <p className="text-sm font-medium text-stone-800 leading-relaxed">
          {q.question_text}
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            rows={4}
            disabled={loading}
            placeholder="Type your answer…"
            className="w-full rounded border border-stone-200 px-3 py-2.5 text-sm text-stone-800 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none resize-y disabled:opacity-60 disabled:cursor-not-allowed"
          />
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleSkip}
              disabled={loading}
              className="text-xs text-stone-500 hover:text-stone-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Skip this question
            </button>
            <button
              type="submit"
              disabled={!answer.trim() || loading}
              className="rounded px-4 py-2 text-sm font-medium text-white bg-stone-800 hover:bg-stone-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin inline-block w-3.5 h-3.5 border border-white/40 border-t-white rounded-full" />
                  Sending…
                </span>
              ) : (
                'Send Response →'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function CandidateCard({
  candidate,
  selected,
  onToggle,
}: {
  candidate: CandidateReview
  selected: boolean
  onToggle: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const formatTag = (t: string) =>
    t.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  const hasDetails = !!(candidate.authority_notes || candidate.role_context)
  const summary = candidate.authority_notes || candidate.role_context || ''

  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="mt-1 h-4 w-4 rounded border-stone-300 text-stone-800 focus:ring-stone-500 cursor-pointer"
      />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-stone-800">
            {candidate.role_title || 'Unknown role'}
          </span>
          {candidate.employer && (
            <span className="text-xs text-stone-500">{candidate.employer}</span>
          )}
          {candidate.period && (
            <span className="text-xs text-stone-400">{candidate.period}</span>
          )}
        </div>

        {candidate.competency_tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {candidate.competency_tags.slice(0, 5).map((tag) => (
              <span
                key={tag}
                className="inline-block rounded bg-stone-100 px-1.5 py-0.5 text-[10px] text-stone-600"
              >
                {formatTag(tag)}
              </span>
            ))}
            {candidate.competency_tags.length > 5 && (
              <span className="text-[10px] text-stone-400">
                +{candidate.competency_tags.length - 5} more
              </span>
            )}
          </div>
        )}

        {hasDetails && (
          <div className="mt-2">
            {expanded ? (
              <div className="space-y-1.5">
                {candidate.authority_notes && (
                  <p className="text-xs text-stone-600 leading-relaxed">
                    <span className="font-medium text-stone-500">Authority: </span>
                    {candidate.authority_notes}
                  </p>
                )}
                {candidate.role_context && (
                  <p className="text-xs text-stone-600 leading-relaxed">
                    <span className="font-medium text-stone-500">Context: </span>
                    {candidate.role_context}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-stone-500 line-clamp-2">{summary}</p>
            )}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setExpanded(!expanded) }}
              className="mt-1 text-[10px] text-stone-400 hover:text-stone-600 transition-colors"
            >
              {expanded ? 'Show less ↑' : 'Show more ↓'}
            </button>
          </div>
        )}

        {candidate.possible_match && (
          <p className="text-xs text-amber-600 mt-1.5">
            ↻ May update existing: {candidate.possible_match.role_title}
            {candidate.possible_match.employer
              ? ` · ${candidate.possible_match.employer}`
              : ''}
          </p>
        )}
      </div>
    </label>
  )
}

function DateConfirmationRow({
  candidate,
}: {
  candidate: CandidateReview
}) {
  const { confirmDate, dateConfirmations } = useFreeformStore()
  const existing = dateConfirmations.find((dc) => dc.temp_id === candidate.temp_id)
  const [editPeriod, setEditPeriod] = useState(candidate.period ?? '')

  return (
    <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
      <p className="font-medium text-amber-800 mb-1">
        {candidate.role_title} · {candidate.employer}
      </p>
      <p className="text-xs text-amber-700 mb-3">
        This role is marked as current ({candidate.period ?? 'no period'}). Is it still active?
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => confirmDate(candidate.temp_id, true)}
          className={`rounded px-3 py-1 text-xs font-medium border transition-colors ${
            existing?.confirmed && !existing?.period
              ? 'bg-amber-600 text-white border-amber-600'
              : 'border-amber-300 text-amber-700 hover:bg-amber-100'
          }`}
        >
          Yes, still current
        </button>
        <span className="flex items-center gap-1.5">
          <input
            type="text"
            value={editPeriod}
            onChange={(e) => setEditPeriod(e.target.value)}
            placeholder="e.g. 2019-2023"
            className="rounded border border-amber-300 bg-white px-2 py-1 text-xs text-stone-800 focus:border-amber-500 focus:outline-none w-28"
          />
          <button
            onClick={() => confirmDate(candidate.temp_id, false, editPeriod)}
            disabled={!editPeriod.trim()}
            className="rounded px-3 py-1 text-xs font-medium border border-amber-300 text-amber-700 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Use this period
          </button>
        </span>
      </div>
    </div>
  )
}

function ReviewStep() {
  const {
    candidates,
    selectedIds,
    toggleSelect,
    selectAll,
    resolve,
    reset,
    loading,
    error,
    pendingDateConfirmations,
    dateConfirmations,
  } = useFreeformStore()

  const hasPending = pendingDateConfirmations.length > 0

  // All pending-confirmation candidates must have a decision
  const allConfirmed = pendingDateConfirmations.every((pc) =>
    dateConfirmations.some((dc) => dc.temp_id === pc.temp_id)
  )

  if (candidates.length === 0) {
    return (
      <div className="max-w-md">
        <p className="text-sm text-stone-500 mb-6">
          No accomplishments could be extracted from that text. Try pasting a more
          detailed section — a performance review, a project summary, or a role
          description with specific outcomes.
        </p>
        <button
          onClick={reset}
          className="rounded px-4 py-2 text-sm font-medium text-white bg-stone-800 hover:bg-stone-700 transition-colors"
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    // Disabled fieldset freezes selection toggles, select-all, and the date-
    // confirmation inputs while the resolve request is in flight.
    <fieldset disabled={loading} className="max-w-2xl min-w-0 border-0 disabled:opacity-70">
      <h2 className="text-base font-semibold text-stone-800 mb-1">
        Review extracted accomplishments
      </h2>
      <p className="text-xs text-stone-400 mb-5">
        {candidates.length} found — select which to add to your library
      </p>

      {hasPending && (
        <div className="mb-5 space-y-3">
          <p className="text-xs font-medium text-amber-700">
            These roles are marked as current. Please confirm or correct before adding.
          </p>
          {pendingDateConfirmations.map((pc) => (
            <DateConfirmationRow key={pc.temp_id} candidate={pc} />
          ))}
        </div>
      )}

      {error && (
        <p className="mb-4 text-xs text-red-600 rounded border border-red-200 bg-red-50 px-3 py-2">
          {error}
        </p>
      )}

      <div className="space-y-3 mb-6">
        {candidates.map((c) => (
          <div
            key={c.temp_id}
            className={`rounded border px-4 py-3 transition-colors ${
              selectedIds.has(c.temp_id)
                ? 'border-stone-300 bg-white'
                : 'border-stone-100 bg-stone-50 opacity-60'
            }`}
          >
            <CandidateCard
              candidate={c}
              selected={selectedIds.has(c.temp_id)}
              onToggle={() => toggleSelect(c.temp_id)}
            />
          </div>
        ))}
      </div>

      {candidates.length > 1 && selectedIds.size < candidates.length && (
        <button
          onClick={selectAll}
          className="mb-4 text-xs text-stone-500 hover:text-stone-700 transition-colors underline underline-offset-2"
        >
          Select all
        </button>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={resolve}
          disabled={selectedIds.size === 0 || loading || (hasPending && !allConfirmed)}
          className="rounded px-4 py-2 text-sm font-medium text-white bg-stone-800 hover:bg-stone-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin inline-block w-3.5 h-3.5 border border-white/40 border-t-white rounded-full" />
              Checking your Library…
            </span>
          ) : (
            `Continue with ${selectedIds.size}`
          )}
        </button>
        {hasPending && !allConfirmed && (
          <p className="text-xs text-amber-600">
            Please confirm the roles marked above first.
          </p>
        )}
      </div>
    </fieldset>
  )
}

function ResolveStep() {
  const { review, apply, reset, loading } = useFreeformStore()
  if (!review) return null
  return (
    // Centered ~720px reading column (DesignGate: "Freeform resolve page container").
    <div className="mx-auto w-full max-w-[720px] flex flex-col h-[calc(100vh-9rem)]">
      <ResolveReview
        review={review}
        applying={loading}
        incomingLabel="From your text"
        title="Review before adding"
        subtitle="Here's how what you added fits with your Library. Nothing is saved until you confirm."
        applyLabel="Add to Library"
        discardLabel="Start over"
        onApply={apply}
        onDiscard={reset}
      />
    </div>
  )
}

function CommittingStep() {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <span className="animate-spin inline-block w-6 h-6 border-2 border-stone-300 border-t-stone-700 rounded-full" />
      <p className="text-sm text-stone-500">Adding to your library…</p>
    </div>
  )
}

function CompleteStep() {
  const { result, reset } = useFreeformStore()

  const added = result?.added_count ?? 0
  const enriched = result?.enriched_count ?? 0
  const message = completionMessage(added, enriched)
  const isSuccess = added > 0 || enriched > 0

  return (
    <div className="max-w-md">
      <p
        className={`text-sm mb-6 ${
          isSuccess ? 'text-stone-700 font-medium' : 'text-stone-500'
        }`}
      >
        {message}
      </p>
      <div className="flex items-center gap-4">
        <Link
          to="/library"
          className="rounded px-4 py-2 text-sm font-medium text-white bg-stone-800 hover:bg-stone-700 transition-colors"
        >
          Back to Library
        </Link>
        <button
          onClick={reset}
          className="text-xs text-stone-500 hover:text-stone-700 transition-colors"
        >
          Paste more experience
        </button>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export default function FreeformPage() {
  const { step, reset } = useFreeformStore()

  useEffect(() => {
    reset()
  }, [reset])

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar />

      <main className="flex-1 ml-16 lg:ml-64 px-6 py-8 max-w-4xl w-full">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/library" className="text-xs text-stone-400 hover:text-stone-600 transition-colors">
            ← Library
          </Link>
        </div>

        {step === 'input' && <InputStep />}
        {step === 'extracting' && <ExtractingStep />}
        {step === 'interrogation' && <InterrogationStep />}
        {step === 'review' && <ReviewStep />}
        {step === 'resolve' && <ResolveStep />}
        {step === 'committing' && <CommittingStep />}
        {step === 'complete' && <CompleteStep />}
      </main>

      <footer className="py-4 text-center text-xs text-stone-400 border-t border-stone-100">
        <a href="/privacy" className="hover:text-stone-600 transition-colors">
          Privacy
        </a>
      </footer>
    </div>
  )
}
